import { useState } from 'react';
import {
  Grid,
  Typography,
  Modal,
  FormControl,
  IconButton,
  InputLabel,
  Select,
  MenuItem,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

export default function CustomerSurveyForm({
  isOpen,
  handleClose,
  setIsSnackbarOpen,
  setSnackbarMessage,
  setSnackbarSeverity,
  retailerId,
  jurisdiction,
  bgColor,
  accentColor,
}) {
  const { t } = useTranslation();
  const [fillOutSurvey, setFillOutSurvey] = useState('');
  const [optedIn, setOptedIn] = useState(false);
  const [form, setForm] = useState({
    fillOutEase: '',
    likelyToRecommend: '',
    likelyToUseAgain: '',
  });

  const fillOutEaseOptions = [
    t('survey.ease_1'),
    t('survey.ease_2'),
    t('survey.ease_3'),
    t('survey.ease_4'),
    t('survey.ease_5'),
  ];

  const likelyOptions = [
    t('survey.likely_1'),
    t('survey.likely_2'),
    t('survey.likely_3'),
    t('survey.likely_4'),
    t('survey.likely_5'),
  ];

  const handleChange = e => {
    const { name, value } = e.target;
    setForm({
      ...form,
      [name]: value,
    });
  };

  const handleOptInOut = () => {
    if (fillOutSurvey === 'no') {
      setOptedIn(false);
      setSnackbarMessage(t('snackbar.survey_thanks'));
      setSnackbarSeverity('info');
      setIsSnackbarOpen(true);
      handleClose();
    } else {
      setOptedIn(true);
    }
  };

  const isFormComplete =
    form.fillOutEase && form.likelyToRecommend && form.likelyToUseAgain;

  const handleSurveySubmit = async () => {
    try {
      // convert value to number before setting form state
      const payload = {
        responses: {
          fillOutEase: Number(form.fillOutEase[0]), // Assuming the format is "1 - ...", we take the first character and convert to number
          likelyToRecommend: Number(form.likelyToRecommend[0]),
          likelyToUseAgain: Number(form.likelyToUseAgain[0]),
        },
        formType: 'texasScratchTicket',
        retailerId: retailerId,
        jurisdiction: jurisdiction,
      };
      await axios({
        method: 'post',
        url: process.env.REACT_APP_ENDPOINT_RSS_SUBMIT_SURVEY,
        data: payload,
      });
      setSnackbarMessage(t('snackbar.survey_feedback'));
      setSnackbarSeverity('success');
      setIsSnackbarOpen(true);
      handleClose();
    } catch (error) {
      console.error('Error submitting survey:', error);
      setSnackbarMessage(t('snackbar.survey_error'));
      setSnackbarSeverity('error');
      setIsSnackbarOpen(true);
    }
  };

  return (
    <Modal open={isOpen} onClose={handleClose}>
      <Grid
        container
        spacing={2}
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '95vw', sm: '90vw', md: 800 },
          bgcolor: bgColor,
          p: 4,
          overflowY: 'auto',
          maxHeight: { xs: '90%', md: '90%' },
        }}
      >
        <Grid
          size={12}
          sx={{ display: 'flex', justifyContent: 'space-between' }}
        >
          <Typography
            id='modal-modal-title'
            sx={{ fontSize: optedIn ? '25px' : '20px', color: '#FFFFFF' }}
          >
            {optedIn
              ? t('modal.survey_title')
              : t('modal.survey_opt_in')}
          </Typography>
          <IconButton
            aria-label='close'
            sx={{
              color: 'white',
              '&:hover': {
                color: 'red',
              },
            }}
            onClick={handleClose}
          >
            <CloseIcon />
          </IconButton>
        </Grid>
        <Grid size={12}>
          {optedIn ? (
            <>
              <FormControl
                fullWidth
                sx={{ marginBottom: '15px', backgroundColor: 'white' }}
                required
              >
                <InputLabel id='fillOutEaseLabel'>
                  {t('survey.fill_out_ease_label')}
                </InputLabel>
                <Select
                  labelId='fillOutEaseLabel'
                  name='fillOutEase'
                  value={form.fillOutEase}
                  label={t('survey.fill_out_ease_label')}
                  onChange={handleChange}
                >
                  {fillOutEaseOptions.map(option => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl
                fullWidth
                sx={{ marginBottom: '15px', backgroundColor: 'white' }}
                required
              >
                <InputLabel id='likelyToRecommendLabel'>
                  {t('survey.likely_recommend_label')}
                </InputLabel>
                <Select
                  labelId='likelyToRecommendLabel'
                  name='likelyToRecommend'
                  value={form.likelyToRecommend}
                  label={t('survey.likely_recommend_label')}
                  onChange={handleChange}
                >
                  {likelyOptions.map(option => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl
                fullWidth
                sx={{ marginBottom: '15px', backgroundColor: 'white' }}
                required
              >
                <InputLabel id='likelyToUseAgainLabel'>
                  {t('survey.likely_use_again_label')}
                </InputLabel>
                <Select
                  labelId='likelyToUseAgainLabel'
                  name='likelyToUseAgain'
                  value={form.likelyToUseAgain}
                  label={t('survey.likely_use_again_label')}
                  onChange={handleChange}
                >
                  {likelyOptions.map(option => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </>
          ) : (
            <FormControl
              sx={{
                color: 'white',
                marginBottom: '15px',
              }}
            >
              <RadioGroup
                aria-labelledby='fillOutSurveyLabel'
                name='fillOutSurvey'
                value={fillOutSurvey}
                onChange={e => setFillOutSurvey(e.target.value)}
              >
                <FormControlLabel
                  value='yes'
                  control={
                    <Radio
                      sx={{
                        color: 'white',
                        '&.Mui-checked': {
                          color: accentColor,
                        },
                      }}
                    />
                  }
                  label={t('survey.yes')}
                />
                <FormControlLabel
                  value='no'
                  control={
                    <Radio
                      sx={{
                        color: 'white',
                        '&.Mui-checked': {
                          color: accentColor,
                        },
                      }}
                    />
                  }
                  label={t('survey.no')}
                />
              </RadioGroup>
            </FormControl>
          )}
        </Grid>
        <Grid
          size={12}
          sx={{
            display: 'flex',
            justifyContent: optedIn ? 'space-between' : 'flex-end',
            gap: 2,
          }}
        >
          {optedIn && (
            <IconButton
              sx={{
                backgroundColor: accentColor,
                color: 'white',
                '&:hover': { backgroundColor: bgColor },
              }}
              onClick={() => setOptedIn(false)}
            >
              <ArrowBackIosNewIcon />
            </IconButton>
          )}
          <IconButton
            sx={{
              backgroundColor: accentColor,
              color: 'white',
              '&:hover': { backgroundColor: bgColor },
            }}
            onClick={optedIn ? handleSurveySubmit : handleOptInOut}
            disabled={optedIn ? !isFormComplete : fillOutSurvey === ''}
          >
            <ArrowForwardIosIcon />
          </IconButton>
        </Grid>
      </Grid>
    </Modal>
  );
}
